/**
 * Gem Stage Pack
 *
 * Creates brilliant-cut gem meshes with physics and theme integration.
 */

import { type Mesh, Quaternion, Vector3 } from "@babylonjs/core";
import {
  EventBus,
  GemManager,
  type StageConfigManager,
  type StagePack,
  type StageThemeInfo,
} from "@tony/nl-babylon";
import type { Theme } from "@/context/ThemeContext";

/**
 * Default gem configuration
 */
const DEFAULT_GEM_CONFIG = {
  count: 5,
  scale: 1,
};

/**
 * Gem stage pack definition
 */
export const gemStagePack: StagePack = {
  applyTheme: (meshes, theme: StageThemeInfo) => {
    // Get the stored gemManager
    const gemManager = (meshes as unknown as { __gemManager?: GemManager })
      .__gemManager;
    if (!gemManager) {
      // Fallback: create a temporary manager for theme application
      const eventBus = new EventBus();
      const tempManager = new GemManager(eventBus);
      for (const mesh of meshes) {
        tempManager.applyThemeColors(
          mesh,
          theme.themeId as Theme,
          theme.isDarkMode,
        );
      }
      return;
    }

    for (const mesh of meshes) {
      gemManager.applyThemeColors(
        mesh,
        theme.themeId as Theme,
        theme.isDarkMode,
      );
    }
  },

  createAssets: async ({ physicsManager, scene }) => {
    // Read config from StageConfigManager (set by StageContext) or use defaults
    const stageConfigManager = (
      scene.metadata as { stageConfigManager?: StageConfigManager } | undefined
    )?.stageConfigManager;
    const stageConfig = stageConfigManager?.config;
    const config = {
      count: stageConfig?.count ?? DEFAULT_GEM_CONFIG.count,
      scale: stageConfig?.scale ?? DEFAULT_GEM_CONFIG.scale,
    };

    console.log("[GemStagePack] Creating with config:", config);

    const eventBus = new EventBus();
    const gemManager = new GemManager(eventBus);
    gemManager.setScene(scene);

    const meshes: Mesh[] = [];

    for (let i = 0; i < config.count; i++) {
      // Create brilliant gem
      const gem = gemManager.createBrilliantGem(scene, {
        followAppTheme: true,
      });

      // Scale the gem
      gem.scaling.setAll(config.scale);

      // Position gems in a circle
      const angle = (i / config.count) * Math.PI * 2;
      const radius = 2 + Math.random() * 1.5;

      // 60% of gems start low (upright), 40% drop from height (tumble)
      const shouldStartUpright = i < config.count * 0.6;

      if (shouldStartUpright) {
        // Start just above ground - minimal tumbling, lands upright
        gem.position.set(
          Math.cos(angle) * radius,
          0.3 + Math.random() * 0.2,
          Math.sin(angle) * radius,
        );
        // Only apply Y rotation
        const randomYRotation = Math.random() * Math.PI * 2;
        const yRotationQuat = Quaternion.RotationAxis(
          Vector3.Up(),
          randomYRotation,
        );
        if (gem.rotationQuaternion) {
          gem.rotationQuaternion =
            gem.rotationQuaternion.multiply(yRotationQuat);
        }
      } else {
        // Drop from height with random orientation
        gem.position.set(
          Math.cos(angle) * radius,
          1.5 + Math.random() * 1.5,
          Math.sin(angle) * radius,
        );
        const randomYRotation = Math.random() * Math.PI * 2;
        const randomTilt = (Math.random() - 0.5) * Math.PI * 0.5;
        const yRotationQuat = Quaternion.RotationAxis(
          Vector3.Up(),
          randomYRotation,
        );
        const tiltQuat = Quaternion.RotationAxis(
          new Vector3(Math.random(), 0, Math.random()).normalize(),
          randomTilt,
        );
        if (gem.rotationQuaternion) {
          gem.rotationQuaternion = gem.rotationQuaternion
            .multiply(yRotationQuat)
            .multiply(tiltQuat);
        }
      }

      // Mark as physics root for InteractionManager
      gem.metadata = { isPhysicsRoot: true };

      // Add physics body with explicit radius to avoid BJS non-uniform scaling warning
      // (gem geometry has non-uniform bounding box, explicit radius bypasses auto-detection)
      physicsManager.addPhysicsBody(gem, {
        angularDamping: 0.8,
        friction: 0.6,
        linearDamping: 0.5,
        mass: 2.0,
        radius: config.scale * 0.5,
        restitution: 0.3,
        shapeType: "SPHERE",
      });

      meshes.push(gem);
    }

    // Store gemManager reference for theme updates
    (meshes as unknown as { __gemManager: GemManager }).__gemManager =
      gemManager;

    return meshes;
  },
  displayName: "Gems",
  id: "gem",

  interactionConfig: {
    hoverEffects: true,
    selectionEffects: true,
  },

  physicsConfig: {
    bodyOptions: {
      angularDamping: 0.8,
      friction: 0.6,
      linearDamping: 0.5,
      mass: 2.0,
      restitution: 0.3,
      shapeType: "SPHERE",
    },
    gravity: new Vector3(0, -9.8, 0),
  },
};
